import { useEffect } from "react";
import DecorativeEllipse from "@/components/DecorativeEllipse";

const modules = [
  {
    number: "01",
    title: "Abundancia Real",
    desc: "Entendé qué es la abundancia desde lo práctico. Reprogramá tu relación con el dinero sin mística ni humo. Resultado: claridad sobre qué significa abundancia para vos y por dónde empezar.",
  },
  {
    number: "02",
    title: "Mapa de Valor",
    desc: "Descubrí exactamente qué ofrecés, por qué alguien lo pagaría y cómo lo comunicás en una frase. Resultado: tu oferta en 1 frase + 3 paquetes definidos.",
  },
  {
    number: "03",
    title: "Sistema de Oportunidades",
    desc: "Construí un flujo semanal predecible de contactos, propuestas y oportunidades de ingreso. Resultado: pipeline activo con mensajes y seguimiento.",
  },
  {
    number: "04",
    title: "Cobrar y Negociar",
    desc: "Aprendé a fijar precios con confianza, negociar sin bajar tu valor y cobrar sin vergüenza. Resultado: banco de guiones listos para DM, WhatsApp y email.",
  },
  {
    number: "05",
    title: "Mantener y Escalar",
    desc: "Organizá tu dinero, automatizá decisiones y creá un sistema que crece mes a mes. Resultado: presupuesto simple + hábito semanal de 20 min.",
  },
];

export default function ModulesSection() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("reveal-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );

    document.querySelectorAll(".reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section className="relative overflow-hidden" style={{ backgroundColor: "#0D0D0D" }}>
      {/* Decorative ellipses */}
      <DecorativeEllipse
        className="top-1/4 -left-32"
        size={384}
        opacity={0.08}
        blur={120}
      />
      <DecorativeEllipse
        className="top-1/2 -left-48 -translate-y-1/2"
        size={512}
        opacity={0.09}
        blur={130}
      />
      <DecorativeEllipse
        className="bottom-1/4 -right-40"
        size={448}
        opacity={0.06}
        blur={140}
      />
      <DecorativeEllipse
        className="top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
        size={600}
        opacity={0.04}
        blur={180}
      />

      {/* Subtle gold gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-gold/[0.015] to-transparent pointer-events-none" />

      <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-8 py-16 md:py-24">
        <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-center text-cream mb-4 reveal">
          El programa en 5 módulos
        </h2>
        <p className="font-sans text-sm md:text-base text-cream/50 text-center mb-12 md:mb-16 max-w-xl mx-auto reveal" style={{ transitionDelay: "100ms" }}>
          Cada módulo tiene video (6–12 min), guía PDF, ebook y plantilla para aplicar inmediatamente.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {modules.map((mod, index) => (
            <div
              key={index}
              className="relative rounded-lg p-5 md:p-6 border border-transparent hover:border-gold/30 transition-all duration-300 group overflow-hidden reveal"
              style={{ backgroundColor: "#1C1C1C", transitionDelay: `${150 + index * 100}ms` }}
            >
              {/* Hover glow */}
              <div className="absolute -top-20 -right-20 w-40 h-40 opacity-0 group-hover:opacity-[0.07] transition-opacity duration-500 pointer-events-none">
                <DecorativeEllipse className="top-0 right-0" size={160} opacity={1} blur={0} />
              </div>
              <span className="font-serif text-4xl md:text-5xl font-bold block mb-3" style={{ color: "#8B7344" }}>
                {mod.number}
              </span>
              <h3 className="font-serif text-lg md:text-xl text-cream mb-2 group-hover:text-gold transition-colors duration-300">
                {mod.title}
              </h3>
              <p className="font-sans text-xs md:text-sm text-cream/50 leading-relaxed">
                {mod.desc}
              </p>
            </div>
          ))}
        </div>

        {/* Anna instructor block */}
        <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12 mt-16 md:mt-24 reveal">
          <div className="relative w-full md:w-1/2 max-w-sm md:max-w-none flex-shrink-0">
            {/* Floating aura behind image */}
            <div className="absolute -inset-3 rounded-2xl bg-gold/10 blur-2xl animate-pulse-glow pointer-events-none" />
            
            <div className="relative rounded-xl overflow-hidden border border-gold/20 shadow-[0_0_40px_rgba(184,153,102,0.1)]">
              <img
                src="https://storage.readdy-site.link/project_files/47bfa220-4dc0-474b-be5e-43bab3838ebf/1b12e013-9978-44cb-b6cf-9740933b7f3e_Captura-de-pantalla-2026-05-22-a-las-12.04.04p.m..webp?v=c08e1d58124b441d9e998521a86bca5a"
                alt="Anna dictando el curso"
                className="w-full h-72 md:h-96 object-cover object-top animate-float"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D]/60 via-transparent to-transparent pointer-events-none" />
            </div>
          </div>

          <div className="text-center md:text-left">
            <p className="font-serif text-sm text-gold tracking-widest uppercase mb-2">
              Tu instructora
            </p>
            <h3 className="font-serif text-2xl md:text-3xl text-cream mb-4">
              Anna te acompaña en cada paso
            </h3>
            <p className="font-sans text-sm md:text-base text-cream/60 leading-relaxed mb-6">
              Más de 8 años ayudando a mujeres a transformar su relación con el dinero. En cada módulo te explico con claridad, te doy las herramientas exactas y te guío para que apliques de inmediato. No teoría vacía: resultados concretos desde la primera semana.
            </p>
            <div className="flex items-center justify-center md:justify-start gap-3 text-gold/80">
              <span className="w-8 h-px bg-gold/40" />
              <span className="font-serif text-sm italic">"Tú puedes. Y yo te muestro cómo."</span>
            </div>
          </div>
        </div>
      </div>
      {/* Divider */}
      <div className="relative z-10 h-px bg-gold-gradient" />
    </section>
  );
}